<script setup lang="ts">
import { ref, reactive, watch } from 'vue'
import type { FormInstance } from 'element-plus'
import { PolicyGroupStatus, type IPolicyGroup, type IPolicyGroupUpdateForm } from '@/types/asicw/policies'
import { usePolicies } from '@/composables/asicw/policies'

const props = defineProps<{ group: IPolicyGroup }>()

const dialogVisible = defineModel<boolean>()

const { updatePolicyGroupMutation } = usePolicies()
const { mutateAsync: updatePolicyGroup, isPending } = updatePolicyGroupMutation

const termsOptions = [1, 2, 3, 6, 12, 18, 24, 36]

// Initialize form with invoice data when available
const formRef = ref<FormInstance>()
const form = reactive<IPolicyGroupUpdateForm>({
  effective_date: new Date(props.group.effective_date),
  initial_term_months: props.group.initial_term_months,
  subsequent_term_months: props.group.subsequent_term_months,
  status: props.group.status,
})

// Watch for changes in props.group and update form values
watch(
  () => props.group,
  (newGroup) => {
    form.effective_date = new Date(newGroup.effective_date)
    form.initial_term_months = newGroup.initial_term_months
    form.subsequent_term_months = newGroup.subsequent_term_months
    form.status = newGroup.status
  },
  { deep: true }
)

const rules = {
  effective_date: [{ required: true, message: 'Effective Date is required', trigger: ['blur', 'change'] }],
  initial_term_months: [{ required: true, message: 'Initial Term is required', trigger: ['blur', 'change'] }],
  subsequent_term_months: [{ required: true, message: 'Subsequent Term is required', trigger: ['blur', 'change'] }],
  status: [{ required: true, message: 'Status is required', trigger: ['blur', 'change'] }],
}

const onSubmit = async () => {
  // Validate form before submission
  await formRef.value?.validate(async (valid: boolean) => {
    if (!valid) {
      return
    }

    try {
      await updatePolicyGroup({ id: props.group.id, form })
      resetFormCloseDialog()
    } catch {
      // Error is handled by mutation error handler
    }
  })
}

const resetFormCloseDialog = () => {
  formRef.value?.resetFields()
  dialogVisible.value = false
}
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    label-position="left"
    title="Update Policy Group"
    :show-close="false"
    width="480px"
    @closed="resetFormCloseDialog"
  >
    <el-form ref="formRef" :model="form" :rules="rules" :label-width="160" label-position="left">
      <el-form-item label="Effective Date" prop="effective_date">
        <el-date-picker v-model="form.effective_date" class="w-[160px]" type="date" placeholder="Effective Date" />
      </el-form-item>

      <el-form-item label="Initial Term" prop="initial_term_months">
        <el-select v-model="form.initial_term_months" class="w-[220px]">
          <el-option v-for="opt in termsOptions" :key="opt" :label="`${opt} months`" :value="opt" />
        </el-select>
      </el-form-item>

      <el-form-item label="Subsequent Term" prop="subsequent_term_months">
        <el-select v-model="form.subsequent_term_months" class="w-[220px]">
          <el-option v-for="opt in termsOptions" :key="opt" :label="`${opt} months`" :value="opt" />
        </el-select>
      </el-form-item>

      <el-form-item label="Status" prop="status">
        <el-select v-model="form.status" class="w-[220px]">
          <el-option v-for="opt in Object.values(PolicyGroupStatus)" :key="opt" :label="opt" :value="opt" />
        </el-select>
      </el-form-item>

      <el-form-item label="New Service Agreement" prop="new_signed_document">
        <RlUploadPdf ref="fileUploaderRef" v-model="form.new_signed_document" :max-size-mb="10" />
      </el-form-item>
    </el-form>

    <!-- Footer actions -->
    <template #footer>
      <el-button @click="resetFormCloseDialog">Cancel</el-button>
      <el-button type="primary" :loading="isPending" @click="onSubmit">Submit</el-button>
    </template>
  </el-dialog>
</template>
